import React from 'react';
import { useTranslation } from 'react-i18next';
import * as types from '../../types';
import useVariantSelector from '../../views/ProductDetails/hooks/useVariantSelector';
import HardwareFirstVariantStep from '../HardwareFirstPickYourFi/HardwareFirstVariantStep';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import styles from './HardwareFirstVariantSheet.module.scss';

interface HardwareFirstVariantSheetProps {
  trigger: JSX.Element;
  variants: types.IVariant[];
  onAdd: (sku: string) => void;
}

const noop = () => undefined;

/** Rendered only while the sheet is open, so every open starts with no size chosen. */
function SheetBody({ variants, onAdd, close }: HardwareFirstVariantSheetProps & { close: () => void }) {
  const { t } = useTranslation();
  const { changeVariantOptions, selectedVariant, hasUpdatedSize } = useVariantSelector({
    onColorChange: noop,
    onSelectedVariantChange: noop,
    onSizeChange: noop,
    variants,
    useQueryParams: false,
  });

  return (
    <div className={styles.container}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('hardware_first_cart_choose_size_color')}</h3>
        <p className={styles.subtitle}>{t('hardware_first_cart_s3_band_fitted')}</p>
      </div>
      <div className={styles.scroll}>
        <HardwareFirstVariantStep
          changeVariantOptions={changeVariantOptions}
          hasUpdatedSize={hasUpdatedSize}
          selectedVariant={selectedVariant}
          variants={variants}
          compact
        />
      </div>
      <div className={styles.foot}>
        <button
          className={styles.cta}
          disabled={!hasUpdatedSize}
          onClick={() => {
            onAdd(selectedVariant.sku);
            close();
          }}
          type="button"
        >
          {t(hasUpdatedSize ? 'hardware_first_add_to_cart' : 'pdp_v2_select_a_size')}
        </button>
      </div>
    </div>
  );
}

/**
 * The Series 3+ band question, asked in the cart over the tile it belongs to. The same picker
 * the product page renders, in a sheet, so a cross-sell add does not send the shopper back to
 * the shop for a size. `nested` because the picker's own sizing guide opens a second modal.
 */
export default function HardwareFirstVariantSheet({ trigger, variants, onAdd }: HardwareFirstVariantSheetProps) {
  return (
    <SimpleDismissableModal
      trigger={trigger}
      containerClassName={styles.modal}
      closeIconClassName={styles.closeIcon}
      nested
    >
      {(close) => <SheetBody trigger={trigger} variants={variants} onAdd={onAdd} close={close} />}
    </SimpleDismissableModal>
  );
}
